/**
 * Fetches the platform access-levels matrix and exposes a `can(permission)`
 * helper for the current authenticated user.
 *
 * Admins always return true. All other roles look up the permission in
 * ``platform_settings.access_levels[role]``.
 */

import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";


export const usePermissions = () => {
    const { user, token } = useAuth();
    const [matrix, setMatrix] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!token) { setMatrix({}); setLoading(false); return; }
        try {
            const r = await axios.get(`${API}/settings/platform`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000,
            });
            setMatrix(r.data?.access_levels || {});
        } catch (_e) {
            // Non-admins may not have access to /settings/platform (returns 403).
            // Treat as "use defaults" — falls through to admin-role bypass or false.
            setMatrix({});
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const role = user?.role || "user";

    const can = useCallback((permission) => {
        if (!permission) return true;
        if (role === "admin") return true;
        if (!matrix || !matrix[role]) return false;
        return !!matrix[role][permission];
    }, [role, matrix]);

    return { can, role, loading, refresh: load };
};
